import { useNavigate } from "react-router-dom";
import { TitlelessCard, ODataGrid, Breakpoints, ResponsiveGridColumn } from "../../../../components";
import CTEyeService from "../../../../services/CTEyeService";
import { useDebouncedFilter } from '../../hooks';
import { formatDateTime } from "../../../../app/dateHelpers";
import { routes, router } from "../../../../app/routes";
import { DataFileInfo, LiveInspectionResponse } from "../../../../data/entities";
import { DashboardSectionHeader } from "./DashboardSectionHeader";

const getMostRecentByClient = async (oDataQuery: string) => {
  const expandedQuery = `$expand=CTEyeClient&$orderby=lastWriteDate desc&${oDataQuery}`;
  return await new CTEyeService().getMostRecentFilesByClientOData(expandedQuery);
};

export function RecentFilesGrid({ liveInspections }: { liveInspections: LiveInspectionResponse[] }) {
  const navigate = useNavigate();
  const latestByClientSearch = useDebouncedFilter();

  const isFileActive = (row: any) =>
    liveInspections.some(i => i.clientId === row.clientId && i.filePath === row.filePath);

  const handleLiveView = (row: any) => {
    const params = new URLSearchParams({ filePath: row.filePath ?? row.path });
    navigate(`/cteye/clients/${row.clientId}/live-view?${params.toString()}`);
  };

  const handleImport = (row: any) => {
    const url = router.getUrl(routes.cteyeClientDetails, new Map([["id", row.clientId]]));
    const openFile: DataFileInfo = {
      fileName: row.fileName,
      path: row.filePath ?? row.path,
      sizeBytes: row.sizeBytes ?? 0,
      fileDate: row.fileDate,
      lastWriteTime: row.lastWriteDate ?? row.lastWriteTime,
    };
    navigate(url, { state: { openFile } });
  };

  const filter = latestByClientSearch.filter
    ? { logic: "or" as const, filters: [
        { field: "description", operator: "contains", value: latestByClientSearch.filter },
        { field: "filePath", operator: "contains", value: latestByClientSearch.filter },
      ]}
    : undefined;

  return (
    <>
      <DashboardSectionHeader
        title="Recent Files Per Agent"
        right={
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search agent or file..."
            onChange={latestByClientSearch.onChange}
            style={{ width: 220 }}
          />
        }
      />
      <TitlelessCard className="p-0" bodyClassName="p-0">
        <ODataGrid
          getData={getMostRecentByClient}
          pageSize={5}
          pageable={true}
          filterable={false}
          filter={filter}
          onRowClick={(e) => {
            const row = e.dataItem;
            if (isFileActive(row)) handleLiveView(row);
            else handleImport(row);
          }}
        >
          <ResponsiveGridColumn 
            title="Agent" 
            field="description" 
            sortable={true} 
            filterable={false} 
          />
          <ResponsiveGridColumn 
            title="File" 
            field="fileName" 
            sortable={false} 
            filterable={false} 
          />
          <ResponsiveGridColumn
            title="Inspection Time"
            field="lastWriteDate"
            sortable={true}
            filterable={false}
            breakpoint={Breakpoints.lg}
            cell={(props) => (
              <td className="text-secondary small">
                {props.dataItem.lastWriteDate ? formatDateTime(new Date(props.dataItem.lastWriteDate)) : "—"}
              </td>
            )}
          />
          <ResponsiveGridColumn
            title="Action"
            field="action"
            sortable={false}
            filterable={false}
            width={130}
            cell={(props) => {
              const row = props.dataItem;
              const active = isFileActive(row);
              return (
                <td>
                  <button
                    className={`btn btn-${active ? 'success' : 'secondary'} btn-sm rounded-pill d-flex align-items-center gap-1`}
                    style={{ fontSize: '0.6875rem', padding: '0.25rem 0.75rem' }}
                    onClick={(e) => { e.stopPropagation(); active ? handleLiveView(row) : handleImport(row); }}
                  >
                    <i className={`fa fa-${active ? 'broadcast-tower' : 'file-import'}`} style={{ fontSize: '0.6rem' }} />
                    {active ? 'Live View' : 'Import'}
                  </button>
                </td>
              );
            }}
          />
        </ODataGrid>
      </TitlelessCard>
    </>
  );
}